import axios from 'axios';

import { getCurrentEnvUrls } from '@/libs/config';
import { getAuthToken } from '@/libs/firebase/auth/auth-helpers';

/**
 * Shop-link autologin client (guardian-view-be `/api/v1/sso/shop-*`). Opening the Shop tab hands
 * the user's proven mobile Firebase session to a fresh system browser that ends up logged into the
 * Shopify store:
 *  1. `shop-link` (Bearer Firebase) mints a one-time code and returns the `/shop-bridge` URL to open.
 *  2. The web `/shop-bridge` calls `shop-exchange` (public) to swap the code for a Firebase custom
 *     token, signs in with it, then follows `shopifyLoginUrl` — which force-clears any stale OIDC
 *     session before bouncing through Shopify → OIDC → the store.
 *
 * Dedicated axios instance (not the shared clients): these routes live on the IdP origin
 * (`ssoIssuerUrl`), and only `shop-link` carries the Firebase bearer — `shop-exchange` is public and
 * stateless (no cookie), so no `withCredentials` here.
 */
const client = axios.create({
  timeout: 15_000,
  headers: { Accept: 'application/json' },
});

const shopUrl = (path: string): string => {
  const base = getCurrentEnvUrls().ssoIssuerUrl.replace(/\/+$/, '');
  return `${base}/api/v1/sso/${path}`;
};

/** Mint a one-time code and return the `/shop-bridge` URL to open in the system browser. */
export async function requestShopLinkUrl(redirectUrl: string): Promise<string> {
  const idToken = await getAuthToken();
  if (!idToken) throw new Error('Not authenticated: missing Firebase ID token');
  const { data } = await client.post<{ url: string }>(
    shopUrl('shop-link'),
    { redirectUrl },
    {
      headers: { Authorization: `Bearer ${idToken}` },
    },
  );
  return data.url;
}

export type ShopExchangeResult = { customToken: string; shopifyLoginUrl: string };

/** Swap the one-time code (from the `/shop-bridge` URL) for a Firebase custom token + next URL. */
export async function exchangeShopCode(code: string): Promise<ShopExchangeResult> {
  const { data } = await client.post<ShopExchangeResult>(shopUrl('shop-exchange'), { code });
  return data;
}
